import { createAction, createSlice } from '@reduxjs/toolkit';

export const loadQrHistoryRequest = createAction('qr/loadQrHistoryRequest');
export const loadQrCountsRequest = createAction('qr/loadQrCountsRequest');
export const downloadQrCodesRequest = createAction('qr/downloadQrCodesRequest');
export const downloadCsvRequest = createAction('qr/downloadCsvRequest');
export const generateQrCodesRequest = createAction('qr/generateQrCodesRequest');
export const checkQrGenerationStatusRequest = createAction('qr/checkQrGenerationStatusRequest');
export const stopPollingRequest = createAction('qr/stopPollingRequest');
export const continuePollingRequest = createAction('qr/continuePollingRequest');
export const softHideQrRunRequest = createAction('qr/softHideQrRunRequest');

const initialState = {
  history: [],
  historyLoaded: false,
  loading: false,
  downloading: false,
  generating: false,
  polling: false,
  currentJob: null, // { submissionId, status, s3Link?, attempts }
  nextToken: null,
  count: 0,
  counts: {
    Total: 0,
    Used: 0,
    Hidden: 0,
    period: 'all time',
    loading: false,
    error: null
  },
  error: null,
};

const qrSlice = createSlice({
  name: 'qr',
  initialState,
  reducers: {
    loadQrHistoryStart(state) {
      state.loading = true;
      state.error = null;
    },
    loadQrHistorySuccess(state, action) {
      state.loading = false;
      state.history = action.payload.history || [];
      state.nextToken = action.payload.nextToken || null;
      state.count = action.payload.count || 0;
      state.historyLoaded = true;
    },
    loadQrHistoryFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
    downloadQrCodesStart(state) {
      state.downloading = true;
      state.error = null;
    },
    downloadQrCodesSuccess(state) {
      state.downloading = false;
    },
    downloadQrCodesFailure(state, action) {
      state.downloading = false;
      state.error = action.payload.error;
    },
    downloadCsvStart(state) {
      state.downloading = true;
      state.error = null;
    },
    downloadCsvSuccess(state) {
      state.downloading = false;
    },
    downloadCsvFailure(state, action) {
      state.downloading = false;
      state.error = action.payload.error;
    },
    loadQrCountsStart(state) {
      state.counts.loading = true;
      state.counts.error = null;
    },
    loadQrCountsSuccess(state, action) {
      state.counts.loading = false;
      state.counts.Total = action.payload.Total || 0;
      state.counts.Used = action.payload.Used || 0;
      state.counts.Hidden = action.payload.Hidden || 0;
      state.counts.period = action.payload.period || 'all time';
    },
    loadQrCountsFailure(state, action) {
      state.counts.loading = false;
      state.counts.error = action.payload.error;
    },
    generateQrCodesStart(state) {
      state.generating = true;
      state.error = null;
      state.currentJob = null;
    },
    generateQrCodesSuccess(state, action) {
      state.generating = false;
      state.polling = true;
      state.currentJob = {
        submissionId: action.payload.submissionId,
        status: 'SUBMITTED',
        attempts: 0,
        s3Link: null,
        startSerialNo: action.payload.startSerialNo
      };
    },
    generateQrCodesFailure(state, action) {
      state.generating = false;
      state.error = action.payload.error;
      state.currentJob = null;
    },
    checkQrGenerationStatusStart(state) {
      state.error = null;
      if (state.currentJob) {
        state.currentJob.attempts += 1;
      }
    },
    checkQrGenerationStatusSuccess(state, action) {
      if (state.currentJob) {
        state.currentJob.status = action.payload.status;
        state.currentJob.s3Link = action.payload.s3Link || state.currentJob.s3Link;

        // Stop polling if completed or failed
        if (action.payload.status === 'COMPLETED' || action.payload.status === 'FAILED') {
          state.polling = false;
        }
      }
    },
    checkQrGenerationStatusFailure(state, action) {
      state.error = action.payload.error;
      // Don't stop polling on temporary errors, let saga handle retry logic
    },
    stopPolling(state) {
      state.polling = false;
    },
    continuePolling(state) {
      state.polling = true;
      if (state.currentJob) {
        state.currentJob.attempts = 0; // Reset attempts when manually continuing
      }
    },
    clearCurrentJob(state) {
      state.currentJob = null;
      state.polling = false;
      state.generating = false;
    },
    softHideQrRunStart(state) {
      state.loading = true;
      state.error = null;
    },
    softHideQrRunSuccess(state, action) {
      state.loading = false;
      // Remove the hidden run from history if it exists
      const hiddenRunId = action.payload.runId;
      state.history = state.history.filter(item => (item.runId || item.runid) !== hiddenRunId);
      // Update count if available
      if (state.count > 0) {
        state.count -= 1;
      }
    },
    softHideQrRunFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
    },
  },
});

export const {
  loadQrHistoryStart,
  loadQrHistorySuccess,
  loadQrHistoryFailure,
  downloadQrCodesStart,
  downloadQrCodesSuccess,
  downloadQrCodesFailure,
  loadQrCountsStart,
  loadQrCountsSuccess,
  loadQrCountsFailure,
  downloadCsvStart,
  downloadCsvSuccess,
  downloadCsvFailure,
  generateQrCodesStart,
  generateQrCodesSuccess,
  generateQrCodesFailure,
  checkQrGenerationStatusStart,
  checkQrGenerationStatusSuccess,
  checkQrGenerationStatusFailure,
  stopPolling,
  continuePolling,
  clearCurrentJob,
  softHideQrRunStart,
  softHideQrRunSuccess,
  softHideQrRunFailure
} = qrSlice.actions;

export default qrSlice.reducer;
